import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Italic,
  Link2,
  List,
  ListOrdered,
  Pilcrow,
  Quote,
  Redo2,
  Sparkles,
  Underline as UnderlineIcon,
  Undo2,
  Unlink2,
} from 'lucide-react';
import { useEditorState } from '@tiptap/react';
import { TOOLBAR_PRESETS } from './richTextEditor.constants.js';
import RichTextEditorDownloadMenu from './RichTextEditorDownloadMenu.jsx';

const BUTTON_SIZE = 14;
const TOOLBAR_BUTTON_BASE_CLASSES =
  'inline-flex min-h-8 min-w-8 items-center justify-center gap-1.5 rounded-md border border-transparent bg-transparent text-[12px] leading-none text-content-secondary transition-colors duration-150 hover:bg-surface-hover-subtle hover:text-content disabled:cursor-not-allowed disabled:opacity-50';
const TOOLBAR_BUTTON_ACTIVE_CLASSES =
  'border-edge-strong bg-surface text-content dark:border-edge-strong dark:bg-surface-elevated';

const TOOLBAR_ACTIONS = {
  paragraph: {
    id: 'paragraph',
    label: 'Paragraph',
    icon: Pilcrow,
    isActive: (editor) => editor.isActive('paragraph'),
    execute: (editor) => editor.chain().focus().setParagraph().run(),
    canRun: (editor) => editor.can().chain().focus().setParagraph().run(),
  },
  heading1: {
    id: 'heading1',
    label: 'Heading 1',
    icon: Heading1,
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
    execute: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    canRun: (editor) => editor.can().chain().focus().toggleHeading({ level: 1 }).run(),
  },
  heading2: {
    id: 'heading2',
    label: 'Heading 2',
    icon: Heading2,
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
    execute: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    canRun: (editor) => editor.can().chain().focus().toggleHeading({ level: 2 }).run(),
  },
  bold: {
    id: 'bold',
    label: 'Bold',
    icon: Bold,
    isActive: (editor) => editor.isActive('bold'),
    execute: (editor) => editor.chain().focus().toggleBold().run(),
    canRun: (editor) => editor.can().chain().focus().toggleBold().run(),
  },
  italic: {
    id: 'italic',
    label: 'Italic',
    icon: Italic,
    isActive: (editor) => editor.isActive('italic'),
    execute: (editor) => editor.chain().focus().toggleItalic().run(),
    canRun: (editor) => editor.can().chain().focus().toggleItalic().run(),
  },
  underline: {
    id: 'underline',
    label: 'Underline',
    icon: UnderlineIcon,
    isActive: (editor) => editor.isActive('underline'),
    execute: (editor) => editor.chain().focus().toggleUnderline().run(),
    canRun: (editor) => editor.can().chain().focus().toggleUnderline().run(),
  },
  code: {
    id: 'code',
    label: 'Inline code',
    icon: Code,
    isActive: (editor) => editor.isActive('code'),
    execute: (editor) => editor.chain().focus().toggleCode().run(),
    canRun: (editor) => editor.can().chain().focus().toggleCode().run(),
  },
  blockquote: {
    id: 'blockquote',
    label: 'Blockquote',
    icon: Quote,
    isActive: (editor) => editor.isActive('blockquote'),
    execute: (editor) => editor.chain().focus().toggleBlockquote().run(),
    canRun: (editor) => editor.can().chain().focus().toggleBlockquote().run(),
  },
  link: {
    id: 'link',
    label: 'Set link',
    icon: Link2,
    isActive: (editor) => editor.isActive('link'),
  },
  unlink: {
    id: 'unlink',
    label: 'Unset link',
    icon: Unlink2,
    isActive: (editor) => false,
    execute: (editor) => editor.chain().focus().unsetLink().run(),
    canRun: (editor) => editor.isActive('link'),
  },
  bulletList: {
    id: 'bulletList',
    label: 'Bullet list',
    icon: List,
    isActive: (editor) => editor.isActive('bulletList'),
    execute: (editor) => editor.chain().focus().toggleBulletList().run(),
    canRun: (editor) => editor.can().chain().focus().toggleBulletList().run(),
  },
  orderedList: {
    id: 'orderedList',
    label: 'Ordered list',
    icon: ListOrdered,
    isActive: (editor) => editor.isActive('orderedList'),
    execute: (editor) => editor.chain().focus().toggleOrderedList().run(),
    canRun: (editor) => editor.can().chain().focus().toggleOrderedList().run(),
  },
  undo: {
    id: 'undo',
    label: 'Undo',
    icon: Undo2,
    isActive: () => false,
    execute: (editor) => editor.chain().focus().undo().run(),
    canRun: (editor) => editor.can().chain().focus().undo().run(),
  },
  redo: {
    id: 'redo',
    label: 'Redo',
    icon: Redo2,
    isActive: () => false,
    execute: (editor) => editor.chain().focus().redo().run(),
    canRun: (editor) => editor.can().chain().focus().redo().run(),
  },
};

function ToolbarButton({
  action,
  editor,
  onExecute,
  disabled = false,
}) {
  const Icon = action.icon;
  const isActive = action.isActive(editor);
  const canRun = action.canRun ? action.canRun(editor) : true;
  const buttonDisabled = disabled || !canRun;

  return (
    <button
      type="button"
      title={action.label}
      aria-label={action.label}
      disabled={buttonDisabled}
      onClick={() => onExecute(action)}
      className={[
        TOOLBAR_BUTTON_BASE_CLASSES,
        isActive ? TOOLBAR_BUTTON_ACTIVE_CLASSES : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon size={BUTTON_SIZE} />
    </button>
  );
}

function resolveGroups(toolbarPreset) {
  if (toolbarPreset === 'custom') return [];
  if (toolbarPreset === 'basic') return TOOLBAR_PRESETS.basic;
  return TOOLBAR_PRESETS.medium;
}

const RichTextEditorToolbar = ({
  editor,
  toolbarPreset = 'medium',
  toolbarActions = [],
  renderToolbarActions,
  disabled = false,
  readOnly = false,
  onRunLinkAction,
  showAiToggle = false,
  aiPanelOpen = false,
  onToggleAiPanel,
  downloadMenuEnabled = false,
  downloadTriggerLabel,
  downloadTriggerAriaLabel,
  downloadPdfLabel,
  downloadDocxLabel,
  downloadFileNameBase,
  onExportStart,
  onExportSuccess,
  onExportError,
}) => {
  /** Re-render on doc/selection changes while parent uses `shouldRerenderOnTransaction: false`. */
  useEditorState({
    editor,
    selector: ({ transactionNumber }) => transactionNumber,
  });

  if (!editor) return null;

  const groups = resolveGroups(toolbarPreset);
  const isToolbarLocked = disabled || readOnly;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-edge bg-surface-muted p-2">
      {groups.map((group, groupIndex) => (
        <div
          key={`group-${groupIndex}`}
          className="mr-1 inline-flex items-center gap-1 border-r border-edge pr-2"
        >
          {group.map((actionId) => {
            const action = TOOLBAR_ACTIONS[actionId];
            if (!action) return null;

            return (
              <ToolbarButton
                key={action.id}
                action={action}
                editor={editor}
                disabled={isToolbarLocked}
                onExecute={(currentAction) => {
                  if (currentAction.id === 'link') {
                    onRunLinkAction?.();
                    return;
                  }
                  currentAction.execute?.(editor);
                }}
              />
            );
          })}
        </div>
      ))}

      {toolbarActions.length > 0 && (
        <div className="mr-1 inline-flex items-center gap-1 border-r border-edge pr-2">
          {toolbarActions.map((action) => {
            const Icon = action.icon;
            const actionDisabled = isToolbarLocked || Boolean(action.disabled);
            return (
              <button
                key={action.id}
                type="button"
                title={action.label}
                aria-label={action.label}
                disabled={actionDisabled}
                onClick={() => action.onClick?.(editor)}
                className={TOOLBAR_BUTTON_BASE_CLASSES}
              >
                {Icon ? <Icon size={BUTTON_SIZE} /> : null}
                <span className="text-[12px] font-medium">{action.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {renderToolbarActions ? (
        <div className="ml-auto inline-flex items-center">
          {renderToolbarActions({ editor, disabled: isToolbarLocked })}
        </div>
      ) : null}

      {downloadMenuEnabled ? (
        <div className="mr-1 inline-flex items-center gap-1 border-r border-edge pr-2">
          <RichTextEditorDownloadMenu
            editor={editor}
            disabled={isToolbarLocked}
            downloadTriggerLabel={downloadTriggerLabel}
            downloadTriggerAriaLabel={downloadTriggerAriaLabel}
            downloadPdfLabel={downloadPdfLabel}
            downloadDocxLabel={downloadDocxLabel}
            downloadFileNameBase={downloadFileNameBase}
            onExportStart={onExportStart}
            onExportSuccess={onExportSuccess}
            onExportError={onExportError}
          />
        </div>
      ) : null}

      {showAiToggle ? (
        <div className="mr-1 inline-flex items-center gap-1 border-r border-edge pr-2">
          <button
            type="button"
            title="Toggle AI actions"
            aria-label="Toggle AI actions"
            disabled={isToolbarLocked}
            onClick={() => onToggleAiPanel?.()}
            className={[
              TOOLBAR_BUTTON_BASE_CLASSES,
              aiPanelOpen ? TOOLBAR_BUTTON_ACTIVE_CLASSES : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Sparkles size={BUTTON_SIZE} />
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default RichTextEditorToolbar;
